/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import {
  type PlacementCandidate,
  type PlacementCandidateBanAction,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { useCreateShoppableQualification } from "@src/appV2/redesign/Roles/api/useCreateShoppableQualification";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { type QueryObserverResult } from "@tanstack/react-query";
import { parseISO } from "date-fns";

import { useCreateChildcareSupportChat } from "../../../api/useCreateChildcareSupportChat";
import { PLACEMENT_TYPE } from "../../../api/useCreatePlacementApplication";
import {
  type GetAvailabilityResponse,
  type InterviewAvailability,
} from "../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { useBookingOrderingCohort } from "../../../hooks/useBookingOrderingCohort";
import {
  PlacementApplicationStatus,
  type PlacementResponse,
} from "../../../types/fetchPlacements.schema";
import { hasBookedInterview } from "../../../utils";
import { getWorkplaceStartHour } from "../../../utils/reliabilityRule";
import { getVerifiedType, PLACEMENT_VERIFIED_TYPES } from "../../../utils/verified-placement";
import { type AlternativeAddressData } from "../../PlacementDetailsPage/hooks/useAlternativeAddress";
import { PLACEMENT_ACTION_TYPE, type PlacementActionType } from "../utils/getPrimaryAction";
import { useApplicationOperations } from "./operations/useApplicationOperations";
import { useInterviewOperations } from "./operations/useInterviewOperations";
import { useNavigationOperations } from "./operations/useNavigationOperations";
import { usePlacementPrimaryAction } from "./usePlacementPrimaryAction";
import { usePlacementState } from "./usePlacementState";

interface UsePlacementActionsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  interviewAvailability: InterviewAvailability;
  isLoadingInterviewAvailability: boolean;
  alternativeAddressData?: AlternativeAddressData;
  showAlternativeAddressInformation: boolean;
  isEasySchedulingPlacement?: boolean;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
  refetchInterviewAvailability: () => Promise<QueryObserverResult<GetAvailabilityResponse>>;
  refetchPlacementCandidate: () => Promise<QueryObserverResult<PlacementCandidate>>;
}

/**
 * Calculate workplace start hour dynamically based on selected interview time.
 * This is needed when user selects a new time that hasn't been persisted yet.
 */
function calculateWorkplaceStartHour(params: {
  interview?: { attributes: { start: string } };
  selectedInterviewTime?: { start: Date };
  reliabilityBanRule?: { startHour: number };
  workerTimezone: string;
  workplaceTimezone: string;
}): Date | undefined {
  const {
    interview,
    selectedInterviewTime,
    reliabilityBanRule,
    workerTimezone,
    workplaceTimezone,
  } = params;

  if ((!isDefined(interview) && !isDefined(selectedInterviewTime)) || !reliabilityBanRule) {
    return undefined;
  }

  return getWorkplaceStartHour({
    interviewStart:
      selectedInterviewTime?.start ??
      (interview?.attributes.start ? parseISO(interview.attributes.start) : undefined),
    workerTimezone,
    workplaceTimezone,
    startHour: reliabilityBanRule.startHour,
  });
}

/**
 * Composes all placement action hooks and provides unified interface.
 * Component only needs to call this one hook.
 *
 * @see usePlacementState - State management
 * @see usePlacementPrimaryAction - Action selection
 * @see useApplicationOperations - Application logic
 * @see useInterviewOperations - Interview logic
 * @see useNavigationOperations - Navigation logic
 */
export function usePlacementActions(params: UsePlacementActionsParams) {
  const {
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    alternativeAddressData,
    showAlternativeAddressInformation,
    isEasySchedulingPlacement = false,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  } = params;

  const state = usePlacementState();

  // Get maxReferralBonusAmount early for primary action calculation
  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const { mutateAsync: createShoppableQualification, isLoading: isCreatingQualification } =
    useCreateShoppableQualification();
  const { mutateAsync: createChildcareSupportChat, isLoading: isCreatingChildcareSupportChat } =
    useCreateChildcareSupportChat();
  const { showSuccessToast, showErrorToast } = useToast();
  const bookingReorderingCohort = useBookingOrderingCohort();

  const primaryActionData = usePlacementPrimaryAction({
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    maxReferralBonusAmount,
  });

  const { primaryAction, derivedData } = primaryActionData;

  const applicationOps = useApplicationOperations({
    placementDetails,
    placementCandidate,
    modals: state.modals,
    applicationState: state.applicationState,
    derivedData,
    refetchPlacement,
  });

  const interviewOps = useInterviewOperations({
    placementDetails,
    placementCandidate,
    modals: state.modals,
    interviewState: state.interviewState,
    locationState: state.locationState,
    derivedData,
    applicationOperations: applicationOps,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  });

  const navigationOps = useNavigationOperations({
    placementDetails,
    placementCandidate,
    primaryAction,
    derivedData,
  });

  const hasMissingOrPendingDocuments = placementDetails.workerRequirements?.some(
    (requirement) => !requirement.attributes.isValid
  );

  // These flow methods represent complete user journeys.
  // Each flow is self-contained and easy to understand.
  //
  // Some flows have "continuation" variants that resume from a specific point
  // (e.g., after a modal closes). These are used by modal callbacks.

  const flows = {
    easySchedulingInterviewRequest: async () => {
      if (derivedData.isChildcareSupportChatEnabled && hasMissingOrPendingDocuments) {
        try {
          await createChildcareSupportChat({
            workerId: derivedData.workerId,
            placementId: placementDetails.id,
          });

          state.childcareChatState.setChildcareChatContinuationAction("easyScheduling");
          state.modals.childcareSupportChat.openModal();
          return;
        } catch {
          showErrorToast("Unable to start support chat. Please try again or contact support.");
          return;
        }
      }

      state.modals.easySchedulingInterviewRequest.openModal();

      await applicationOps.handleApply({
        placementType: PLACEMENT_TYPE.verified,
        skipSuccessModal: true,
        // Don't send workerInterviewRequest at all - it's optional
        // This is a "bare bones" application where the workplace reaches out to schedule
      });

      await Promise.all([refetchPlacement(), refetchPlacementCandidate()]);
    },

    applyToJob: async () => {
      if (derivedData.isChildcareSupportChatEnabled && hasMissingOrPendingDocuments) {
        try {
          await createChildcareSupportChat({
            workerId: derivedData.workerId,
            placementId: placementDetails.id,
          });

          state.childcareChatState.setChildcareChatContinuationAction("apply");
          state.modals.childcareSupportChat.openModal();
          return;
        } catch {
          showErrorToast("Unable to start support chat. Please try again or contact support.");
          return;
        }
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        if (derivedData.isJobRequestEnabled) {
          state.modals.createApplication.openModal();
        } else {
          await applicationOps.handleApply({
            placementType: PLACEMENT_TYPE.verified,
            preferences,
          });
        }
      });
    },

    bookInterview: async () => {
      const verifiedType = getVerifiedType(placementDetails);
      const isBookFirstCohort = bookingReorderingCohort === "book_first";

      // For book_first cohort, skip all confirmation dialogs and go directly to interview booking
      if (
        isBookFirstCohort &&
        !placementCandidate.screeningStatus?.startsWith(PlacementCandidateScreeningStatus.PASSED) &&
        verifiedType !== PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB &&
        placementDetails.isRecruiterScreeningEnabled
      ) {
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
        return;
      }

      const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);
      const shouldShowAlternativeAddress =
        showAlternativeAddressInformation && isDefined(alternativeAddressData);

      if (isProspectWorkplace) {
        state.modals.walkInNote.openModal();
        // Note: Modal's onContinue callback will call the actual booking flow
        // by calling interviewOps.openBookInterviewModal directly
        return;
      }

      if (shouldShowAlternativeAddress) {
        state.modals.showAlternativeAddress.openModal();
        // Note: Modal's onContinue callback will call the actual booking flow
        // by calling interviewOps.openBookInterviewModal directly
        return;
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        // Store preferences for the interview booking
        applicationOps.setApplicationPreferences(preferences);
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
      });
    },

    rescheduleInterview: async () => {
      // Check if rescheduling today after workplace start hour (simplified ban)
      if (
        derivedData.hiringReliability.simplifiedBanEnabled &&
        derivedData.isLateCancellation &&
        derivedData.isSchedulingTodayAfterWorkplaceStartHour
      ) {
        state.modals.lateRescheduleToday.openModal();
        return;
      }

      // The modal handles all remaining steps via its callbacks
      await interviewOps.openBookInterviewModal(
        PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
        interviewAvailability
      );
    },

    cancelPastInterview: () => {
      state.modals.cancelPastInterview.openModal();
    },

    completeProfile: () => {
      navigationOps.handleCompleteProfileAction();
    },

    uploadLicense: () => {
      navigationOps.handleUploadLicenseAction();
    },

    uploadDocuments: () => {
      navigationOps.handleUploadDocumentAction();
    },

    uploadCredential: () => {
      navigationOps.handleUploadCredentialAction();
    },

    addQualification: async () => {
      if (!isDefined(derivedData.missingAutoApprovedQualificationId)) {
        showErrorToast("Unable to add role. Please try again.");
        return;
      }

      try {
        await createShoppableQualification({
          workerId: derivedData.workerId,
          qualificationId: derivedData.missingAutoApprovedQualificationId,
        });

        showSuccessToast("Role added successfully!");

        // Refetch to update the UI with new data
        await Promise.all([refetchPlacement(), refetchPlacementCandidate()]);
      } catch {
        showErrorToast("Unable to add role. Please try again or contact support.");
      }
    },

    applyViaExternalLink: async () => {
      if (primaryAction.label === "Apply") {
        state.modals.applyForCandidate.openModal();
        // Note: Modal handles the rest of the flow via its onApply callback
      } else {
        // Regular external application path
        await applicationOps.openExternalApplication();
      }
    },

    talkToRecruiter: async () => {
      await navigationOps.handleTalkToRecruiterAction();
    },

    // These continuation flows resume a user journey from a specific point,
    // typically called from modal callbacks.

    continueBookInterview: async () => {
      const isBookFirstCohort = bookingReorderingCohort === "book_first";

      // For book_first cohort, skip preferences dialog and go directly to interview booking
      if (isBookFirstCohort) {
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
        return;
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        applicationOps.setApplicationPreferences(preferences);
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
      });
    },
  };

  const handlePrimaryAction = async () => {
    // Easy scheduling: Intercept APPLY and BOOK_INTERVIEW actions for eligible placements
    const isEasySchedulingAction =
      (primaryAction.type === PLACEMENT_ACTION_TYPE.APPLY && derivedData.isJobRequestEnabled) ||
      primaryAction.type === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW;

    if (isEasySchedulingPlacement && isEasySchedulingAction) {
      await flows.easySchedulingInterviewRequest();
      return;
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (primaryAction.type) {
      case PLACEMENT_ACTION_TYPE.APPLY: {
        await flows.applyToJob();
        break;
      }

      case PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW: {
        await flows.bookInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW: {
        await flows.rescheduleInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.LATE_CANCEL: {
        flows.cancelPastInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.COMPLETE_PROFILE: {
        flows.completeProfile();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE: {
        flows.uploadLicense();
        break;
      }

      case PLACEMENT_ACTION_TYPE.ADD_QUALIFICATION: {
        await flows.addQualification();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT: {
        flows.uploadDocuments();
        break;
      }

      case PLACEMENT_ACTION_TYPE.APPLY_VIA_WEBLINK: {
        await flows.applyViaExternalLink();
        break;
      }

      case PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER: {
        await flows.talkToRecruiter();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL: {
        flows.uploadCredential();
        break;
      }

      default: {
        // No action - user has already applied or action is disabled
        break;
      }
    }
  };

  const allowedRestrictedActions: string[] = [
    PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
    PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT,
    PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL,
    PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE,
  ];

  const shouldDisablePrimaryAction =
    placementCandidate.isRestricted === true &&
    primaryAction.type !== undefined &&
    !allowedRestrictedActions.includes(primaryAction.type);

  const primaryActionProps = {
    type: primaryAction.type,
    label: primaryAction.label,
    description: primaryAction.description,
    warning: primaryAction.warning,
    onClick: handlePrimaryAction,
    disabled: shouldDisablePrimaryAction || primaryAction.disabled,
    isLoading:
      applicationOps.isCreatingPlacementApplication ||
      interviewOps.isCreatingInterview ||
      isCreatingQualification,
    startIcon: primaryAction.startIcon,
    additionalContent: primaryAction.additionalContent,
  };

  // Helper functions that modals need access to
  const getBansCountByAction = (action: PlacementCandidateBanAction) => {
    return placementCandidate.bansCountByAction?.[action] ?? 0;
  };

  const getPenaltyByAction = (action: PlacementCandidateBanAction) => {
    const { penaltyMappers } = derivedData.hiringReliability;
    const bansCount = getBansCountByAction(action);
    return penaltyMappers[action][String(bansCount + 1)];
  };

  const workplaceStartHour = calculateWorkplaceStartHour({
    interview: derivedData.interview,
    selectedInterviewTime: interviewOps.selectedInterviewTime,
    reliabilityBanRule: placementDetails.workplace.reliabilityBanRule,
    workerTimezone: derivedData.workerTimezone,
    workplaceTimezone: placementDetails.workplace.tmz,
  });

  const isBookFirstCohort = bookingReorderingCohort === "book_first";

  // Status calculations
  const hasAcceptedApplication = isDefined(
    placementDetails.applications?.find(
      (application) => application.attributes.status === PlacementApplicationStatus.ACCEPTED
    )
  );
  const isInterviewBooked = hasBookedInterview(placementDetails);
  const canScheduleInterviewFromDeepLink = interviewAvailability.length > 0;
  const actionType: PlacementActionType = isInterviewBooked
    ? PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW
    : PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW;

  // Return clean interface
  return {
    // Primary action
    primaryAction,
    primaryActionProps,

    // User flows
    flows,

    // Modal states
    modals: state.modals,

    // Interview state & operations
    interview: {
      selectedInterviewTime: interviewOps.selectedInterviewTime,
      setSelectedInterviewTime: interviewOps.setSelectedInterviewTime,
      bookInterviewModalType: interviewOps.bookInterviewModalType,
      interviewData: derivedData.interview,
      openBookInterviewModal: interviewOps.openBookInterviewModal,
      continueAfterPhoneConfirmation: interviewOps.continueAfterPhoneConfirmation,
      handleBookOrRescheduleInterview: interviewOps.handleBookOrRescheduleInterview,
      shouldShowLateRescheduleModal: interviewOps.shouldShowLateRescheduleModal,
      isLateCancellation: derivedData.isLateCancellation,
      isReschedulingPastInterview: derivedData.isReschedulingPastInterview,
      checkLocationPermissionsAfterInterviewBooking:
        interviewOps.checkLocationPermissionsAfterInterviewBooking,
      isOsWithoutAlwaysAllow: interviewOps.isOsWithoutAlwaysAllow,
      isRescheduleToFutureDay: interviewOps.isRescheduleToFutureDay,
      isRescheduleForToday: interviewOps.isRescheduleForToday,
      workplaceStartHour,
      isBookFirstCohort,
    },

    // Application state & operations
    application: {
      handleApply: applicationOps.handleApply,
      applicationPreferences: applicationOps.applicationPreferences,
      setApplicationPreferences: applicationOps.setApplicationPreferences,
      applicationManagementType: applicationOps.applicationManagementType,
      setApplicationManagementType: applicationOps.setApplicationManagementType,
      openExternalApplication: applicationOps.openExternalApplication,
      handleFetchApplicationPreferences: applicationOps.handleFetchApplicationPreferences,
      pendingActionRef: applicationOps.pendingActionRef,
    },

    // Helper functions
    helpers: {
      getBansCountByAction,
      getPenaltyByAction,
    },

    // Derived data for modals
    derivedData: {
      workerId: derivedData.workerId,
      workerTimezone: derivedData.workerTimezone,
      interview: derivedData.interview,
      highestRankingSharedQualification: derivedData.highestRankingSharedQualification,
      hiringReliability: derivedData.hiringReliability,
      requirement: derivedData.requirement,
      isProfilePending: derivedData.isProfilePending,
      workplaceStartHour: derivedData.workplaceStartHour,
      isSchedulingTodayAfterWorkplaceStartHour:
        derivedData.isSchedulingTodayAfterWorkplaceStartHour,
      startsInWorkplaceToday: derivedData.startsInWorkplaceToday,
      eventContext: derivedData.eventContext,
      hasMissingOrPendingDocuments,
    },

    // Recruiter flow
    recruiterScreeningCallFlowState: navigationOps.recruiterScreeningCallFlowState,

    // Childcare chat state
    childcareChatState: state.childcareChatState,

    // Status
    status: {
      hasAcceptedApplication,
      hasBookedInterview,
      canScheduleInterviewFromDeepLink,
      actionType,
    },

    // Loading states
    isLoading:
      applicationOps.isCreatingPlacementApplication ||
      interviewOps.isCreatingInterview ||
      isCreatingQualification ||
      isCreatingChildcareSupportChat,
  };
}

export type PlacementActionsReturn = ReturnType<typeof usePlacementActions>;
